// src/utils/formatters.ts — Pure utility functions (no JSX — see components/StatusBadge.tsx)
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns'

export const formatCurrency = (amount: number, decimals = 0) =>
  `$${Number(amount).toLocaleString('en-AU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`

export const formatDate = (date: string | Date | null | undefined) => {
  if (!date) return '—'
  const d = new Date(date)
  if (isNaN(d.getTime())) return '—'
  return format(d, 'd MMM yyyy')
}

export const formatDateTime = (date: string | Date) =>
  format(new Date(date), 'd MMM yyyy, h:mm a')

export const formatRelative = (date: string | Date) => {
  const d = new Date(date)
  if (isToday(d)) return `Today ${format(d, 'h:mm a')}`
  if (isYesterday(d)) return `Yesterday ${format(d, 'h:mm a')}`
  return formatDistanceToNow(d, { addSuffix: true })
}

export const formatPercent = (value: number) => `${Math.round(value)}%`

export const initialsFrom = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()

export const tierLabel = (tier: string) =>
  ({ A_DEVELOPER: 'Admin', B_PM: 'Project Manager', C_PROCUREMENT: 'Procurement', D_FIELD: 'Field' }[tier] ?? tier)

export const statusMeta = (status: string): { bg: string; text: string; label: string } => {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    NOT_STARTED:    { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Not started' },
    PRE_START:      { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Pre-start' },
    IN_PROGRESS:    { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'In progress' },
    PROCUREMENT:    { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Procurement' },
    BOM_ENTRY:      { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'BOM entry' },
    COMPLETED:      { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Completed' },
    CLOSED:         { bg: 'bg-gray-200',   text: 'text-gray-500',   label: 'Closed' },
    PENDING:        { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Pending' },
    APPROVED:       { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Approved' },
    REJECTED:       { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Rejected' },
    AI_DRAFT:       { bg: 'bg-purple-100', text: 'text-purple-700', label: 'AI draft' },
    DRAFT:          { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Draft' },
    SENT:           { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Sent' },
    DELIVERED:      { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Delivered' },
    REGO:           { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Rego due' },
    SERVICE_TIME:   { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Service due' },
    SERVICE_KM:     { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Service (km)' },
    CALIBRATION:    { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Calibration due' },
    ALLOCATED:      { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Allocated' },
    AVAILABLE:      { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Available' },
    IN_SERVICE:     { bg: 'bg-orange-100', text: 'text-orange-700', label: 'In service' },
  }
  return map[status] ?? { bg: 'bg-gray-100', text: 'text-gray-600', label: status }
}
